import React from "react";
import { Download, FileText, RefreshCw, Send } from "lucide-react";
import type { Ok } from "@ps-bridge/ok";
import type { OptionTs } from "@ps-bridge/interop";
import type {
  StatementClassifierAmount,
  StatementClassifierJobStatus,
  StatementClassifierPreviewRow,
} from "@services/clients/StatementClassifierClient";
import * as Option from "@utils/Option";
import EmptyView from "@view/EmptyView";
import * as styles from "@styles/StatementClassifier.css";

export type StatementClassifierPhase =
  | "featureUnavailable"
  | "idle"
  | "ready"
  | "submitting"
  | "processingDelayed"
  | "workbookReadyPreviewLoading"
  | "previewReady"
  | "previewUnavailable"
  | "downloading"
  | "downloaded"
  | "error";

export type StatementClassifierPanelProps = {
  readonly featureEnabled: boolean;
  readonly programName: string;
  readonly phase: StatementClassifierPhase;
  readonly selectedFiles: readonly File[];
  readonly guidance: string;
  readonly businessDescription: string;
  readonly businessExamples: string;
  readonly personalExamples: string;
  readonly ambiguousRules: string;
  readonly extraNotes: string;
  readonly classificationAdjustments: string;
  readonly message: string;
  readonly status: OptionTs<StatementClassifierJobStatus>;
  readonly previewRows: readonly StatementClassifierPreviewRow[];
  readonly workbookReady: boolean;
  readonly onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => Ok;
  readonly onGuidanceChange: (value: string) => Ok;
  readonly onBusinessDescriptionChange: (value: string) => Ok;
  readonly onBusinessExamplesChange: (value: string) => Ok;
  readonly onPersonalExamplesChange: (value: string) => Ok;
  readonly onAmbiguousRulesChange: (value: string) => Ok;
  readonly onExtraNotesChange: (value: string) => Ok;
  readonly onClassificationAdjustmentsChange: (value: string) => Ok;
  readonly onSubmit: () => Promise<Ok>;
  readonly onCheckStatus: () => Promise<Ok>;
  readonly onRetryPreview: () => Promise<Ok>;
  readonly onDownload: () => Promise<Ok>;
};

const pageStateByPhase = {
  featureUnavailable: "ready",
  idle: "ready",
  ready: "ready",
  submitting: "loading",
  processingDelayed: "ready",
  workbookReadyPreviewLoading: "loading",
  previewReady: "ready",
  previewUnavailable: "ready",
  downloading: "loading",
  downloaded: "ready",
  error: "ready",
} as const satisfies Record<StatementClassifierPhase, "ready" | "loading">;

const statusTitleByPhase = {
  featureUnavailable: "Feature unavailable",
  idle: "Ready for statements",
  ready: "Ready to submit",
  submitting: "Classifying",
  processingDelayed: "Still processing",
  workbookReadyPreviewLoading: "Loading preview",
  previewReady: "Preview ready",
  previewUnavailable: "Workbook ready",
  downloading: "Preparing workbook",
  downloaded: "Workbook downloaded",
  error: "Needs attention",
} as const satisfies Record<StatementClassifierPhase, string>;

const statusClassByPhase = {
  featureUnavailable: `${styles.status} ${styles.statusWarning}`,
  idle: styles.status,
  ready: `${styles.status} ${styles.statusReady}`,
  submitting: `${styles.status} ${styles.statusWarning}`,
  processingDelayed: `${styles.status} ${styles.statusWarning}`,
  workbookReadyPreviewLoading: `${styles.status} ${styles.statusWarning}`,
  previewReady: `${styles.status} ${styles.statusReady}`,
  previewUnavailable: `${styles.status} ${styles.statusReady}`,
  downloading: `${styles.status} ${styles.statusWarning}`,
  downloaded: `${styles.status} ${styles.statusReady}`,
  error: `${styles.status} ${styles.statusDanger}`,
} as const satisfies Record<StatementClassifierPhase, string>;

const submitDisabledByPhase = {
  featureUnavailable: true,
  idle: true,
  ready: false,
  submitting: true,
  processingDelayed: false,
  workbookReadyPreviewLoading: true,
  previewReady: false,
  previewUnavailable: false,
  downloading: true,
  downloaded: false,
  error: false,
} as const satisfies Record<StatementClassifierPhase, boolean>;

const downloadDisabledByPhase = {
  featureUnavailable: true,
  idle: true,
  ready: true,
  submitting: true,
  processingDelayed: true,
  workbookReadyPreviewLoading: false,
  previewReady: false,
  previewUnavailable: false,
  downloading: true,
  downloaded: false,
  error: true,
} as const satisfies Record<StatementClassifierPhase, boolean>;

const checkStatusVisibleByPhase = {
  featureUnavailable: false,
  idle: false,
  ready: false,
  submitting: false,
  processingDelayed: true,
  workbookReadyPreviewLoading: false,
  previewReady: false,
  previewUnavailable: false,
  downloading: false,
  downloaded: false,
  error: false,
} as const satisfies Record<StatementClassifierPhase, boolean>;

const retryPreviewVisibleByPhase = {
  featureUnavailable: false,
  idle: false,
  ready: false,
  submitting: false,
  processingDelayed: false,
  workbookReadyPreviewLoading: false,
  previewReady: false,
  previewUnavailable: true,
  downloading: false,
  downloaded: false,
  error: false,
} as const satisfies Record<StatementClassifierPhase, boolean>;

const formatBytes = (size: number): string => {
  if (size < 1024) {
    return `${size} B`;
  }
  const kib = size / 1024;
  if (kib < 1024) {
    return `${kib.toFixed(1)} KiB`;
  }
  return `${(kib / 1024).toFixed(1)} MiB`;
};

const formatAmount = (amount: OptionTs<StatementClassifierAmount>): string =>
  Option.renderOptionText(amount, "-", (value) => value.toFixed(2));

const formatReasoning = (row: StatementClassifierPreviewRow): string =>
  Option.renderOptionText(row.reasoning, "-", (value) => value);

const SelectedFiles: React.FC<{ readonly files: readonly File[] }> = ({ files }) => {
  const phase = files.length === 0 ? "empty" : "selected";
  const view = {
    empty: <p className={styles.statusText}>No files selected.</p>,
    selected: (
      <div className={styles.list} data-testid="statement-classifier-selected-files">
        {files.map((file) => (
          <div className={styles.fileRow} key={`${file.name}-${file.size}-${file.lastModified}`}>
            <span>{file.name}</span>
            <span>{formatBytes(file.size)}</span>
          </div>
        ))}
      </div>
    ),
  } as const satisfies Record<"empty" | "selected", React.ReactElement>;

  return view[phase];
};

const PreviewRows: React.FC<{ readonly rows: readonly StatementClassifierPreviewRow[] }> = ({ rows }) => {
  const phase = rows.length === 0 ? "empty" : "loaded";
  const view = {
    empty: <div className={styles.emptyState}>Submit a statement job to load preview rows.</div>,
    loaded: (
      <div className={styles.tableWrap}>
        <table className={styles.table} data-testid="statement-classifier-preview-table">
          <thead className={styles.tableHead}>
            <tr>
              <th className={styles.tableCell}>Date</th>
              <th className={styles.tableCell}>Description</th>
              <th className={styles.tableCell}>Amount</th>
              <th className={styles.tableCell}>Classification</th>
              <th className={styles.tableCell}>Confidence</th>
              <th className={styles.tableCell}>Reasoning</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.fileName}-${row.date}-${row.description}`}>
                <td className={styles.tableCell}>{row.date}</td>
                <td className={styles.tableCell}>{row.description}</td>
                <td className={styles.tableCell}>{formatAmount(row.amount)}</td>
                <td className={styles.tableCell}>{row.classification}</td>
                <td className={styles.tableCell}>{row.confidence.toFixed(2)}</td>
                <td className={styles.tableCell}>{formatReasoning(row)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
  } as const satisfies Record<"empty" | "loaded", React.ReactElement>;

  return view[phase];
};

const StatusDetails: React.FC<{ readonly status: OptionTs<StatementClassifierJobStatus> }> = ({ status }) =>
  Option.fold(
    status,
    () => <EmptyView />,
    (value) => (
      <p className={styles.statusText} data-testid="statement-classifier-job-status">
        Job {value.jobId} is {value.phase}.
      </p>
    ),
  );

const CheckStatusButton: React.FC<{
  readonly phase: StatementClassifierPhase;
  readonly onCheckStatus: () => Promise<Ok>;
}> = ({ phase, onCheckStatus }) => {
  const buttonPhase = checkStatusVisibleByPhase[phase] ? "visible" : "hidden";
  const view = {
    hidden: <EmptyView />,
    visible: (
      <button
        className={styles.secondaryButton}
        type="button"
        onClick={() => {
          onCheckStatus().catch(() => undefined);
        }}
        data-testid="statement-classifier-check-status"
      >
        <RefreshCw size={16} />
        Check status
      </button>
    ),
  } as const satisfies Record<"hidden" | "visible", React.ReactElement>;

  return view[buttonPhase];
};

const RetryPreviewButton: React.FC<{
  readonly phase: StatementClassifierPhase;
  readonly onRetryPreview: () => Promise<Ok>;
}> = ({ phase, onRetryPreview }) => {
  const buttonPhase = retryPreviewVisibleByPhase[phase] ? "visible" : "hidden";
  const view = {
    hidden: <EmptyView />,
    visible: (
      <button
        className={styles.secondaryButton}
        type="button"
        onClick={() => {
          onRetryPreview().catch(() => undefined);
        }}
        data-testid="statement-classifier-retry-preview"
      >
        <RefreshCw size={16} />
        Retry preview
      </button>
    ),
  } as const satisfies Record<"hidden" | "visible", React.ReactElement>;

  return view[buttonPhase];
};

const EnabledPanel: React.FC<StatementClassifierPanelProps> = ({
  phase,
  programName,
  selectedFiles,
  guidance,
  businessDescription,
  businessExamples,
  personalExamples,
  ambiguousRules,
  extraNotes,
  classificationAdjustments,
  message,
  status,
  previewRows,
  workbookReady,
  onFileChange,
  onGuidanceChange,
  onBusinessDescriptionChange,
  onBusinessExamplesChange,
  onPersonalExamplesChange,
  onAmbiguousRulesChange,
  onExtraNotesChange,
  onClassificationAdjustmentsChange,
  onSubmit,
  onCheckStatus,
  onRetryPreview,
  onDownload,
}) => (
  <section
    className={styles.page}
    data-testid="page-statement-classifier"
    data-page-state={pageStateByPhase[phase]}
    data-program={programName}
  >
    <div className={styles.shell}>
      <header className={styles.header}>
        <p className={styles.eyebrow}>Statement Classifier</p>
        <h1 className={styles.title}>Classify statement rows into workbook-ready categories.</h1>
        <p className={styles.subtitle}>
          Upload statement files, tune the classification guidance, review the preview rows, and download the workbook.
        </p>
      </header>

      <div className={styles.layout}>
        <div className={styles.panel}>
          <div className={statusClassByPhase[phase]} role={phase === "error" ? "alert" : "status"}>
            <h2 className={styles.statusHeading}>{statusTitleByPhase[phase]}</h2>
            <p className={styles.statusText}>{message}</p>
            <StatusDetails status={status} />
          </div>
          <form
            className={styles.form}
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit().catch(() => undefined);
            }}
          >
            <label className={styles.field}>
              <span className={styles.label}>Statement files</span>
              <input
                className={styles.fileInput}
                aria-label="Statement files"
                type="file"
                multiple
                accept=".pdf,.txt"
                onChange={onFileChange}
              />
            </label>
            <SelectedFiles files={selectedFiles} />
            <label className={styles.field}>
              <span className={styles.label}>Business description</span>
              <textarea
                className={styles.textarea}
                aria-label="Business description"
                value={businessDescription}
                onChange={(event) => onBusinessDescriptionChange(event.target.value)}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Business examples</span>
              <textarea
                className={styles.textarea}
                aria-label="Business examples"
                value={businessExamples}
                onChange={(event) => onBusinessExamplesChange(event.target.value)}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Personal examples</span>
              <textarea
                className={styles.textarea}
                aria-label="Personal examples"
                value={personalExamples}
                onChange={(event) => onPersonalExamplesChange(event.target.value)}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Ambiguous or unknown rules</span>
              <textarea
                className={styles.textarea}
                aria-label="Ambiguous or unknown rules"
                value={ambiguousRules}
                onChange={(event) => onAmbiguousRulesChange(event.target.value)}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Extra notes</span>
              <textarea
                className={styles.textarea}
                aria-label="Extra notes"
                value={extraNotes}
                onChange={(event) => onExtraNotesChange(event.target.value)}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Contextual classification adjustments</span>
              <textarea
                className={styles.textarea}
                aria-label="Contextual classification adjustments"
                value={classificationAdjustments}
                onChange={(event) => onClassificationAdjustmentsChange(event.target.value)}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Base classification guidance</span>
              <textarea
                className={styles.textarea}
                aria-label="Base classification guidance"
                value={guidance}
                onChange={(event) => onGuidanceChange(event.target.value)}
              />
            </label>
            <div className={styles.buttonRow}>
              <button
                className={styles.button}
                type="submit"
                disabled={submitDisabledByPhase[phase] || selectedFiles.length === 0}
                data-testid="statement-classifier-submit"
              >
                <Send size={16} />
                Run classification
              </button>
              <CheckStatusButton phase={phase} onCheckStatus={onCheckStatus} />
              <RetryPreviewButton phase={phase} onRetryPreview={onRetryPreview} />
              <button
                className={styles.secondaryButton}
                type="button"
                disabled={downloadDisabledByPhase[phase] || !workbookReady}
                onClick={() => {
                  onDownload().catch(() => undefined);
                }}
                data-testid="statement-classifier-download"
              >
                <Download size={16} />
                Download workbook
              </button>
            </div>
          </form>
        </div>

        <div className={styles.panel}>
          <div className={`${styles.status} ${styles.statusReady}`}>
            <h2 className={styles.statusHeading}>
              <FileText size={18} /> Preview rows
            </h2>
            <p className={styles.statusText}>Rows shown here come from the current classification job.</p>
          </div>
          <div className={styles.preview}>
            <PreviewRows rows={previewRows} />
          </div>
        </div>
      </div>
    </div>
  </section>
);

const FeatureUnavailablePanel: React.FC = () => (
  <section className={styles.page} data-testid="page-statement-classifier-unavailable" data-page-state="ready">
    <div className={styles.shell}>
      <div className={styles.panel}>
        <div className={`${styles.status} ${styles.statusWarning}`}>
          <h1 className={styles.statusHeading}>Statement Classifier is not enabled.</h1>
          <p className={styles.statusText}>This route is hidden unless the rollout flag is enabled.</p>
        </div>
      </div>
    </div>
  </section>
);

const StatementClassifierPagePanel: React.FC<StatementClassifierPanelProps> = (props) => {
  const featurePhase = props.featureEnabled ? "enabled" : "unavailable";
  const view = {
    enabled: <EnabledPanel {...props} />,
    unavailable: <FeatureUnavailablePanel />,
  } as const satisfies Record<"enabled" | "unavailable", React.ReactElement>;

  return view[featurePhase];
};

export default StatementClassifierPagePanel;
